// ===========================================
// Live totals in the amend dialog (2026-09-28)
// ===========================================
// "Current total $X → New total $Y" with the new subtotal / discounts / GST,
// worked out by the SERVER running the real amendment and rolling it back
// (POST …/amend/preview). Andrew, 2026-09-28: the old line added the lines up in
// the browser and ignored discounts and GST, so it could show "$2,525.00 →
// $2,525.00" while the family's total was about to change — worse than no line.
// The browser never does invoice money; it only shows the server's answer.

import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import type { AmendPreview } from '../../api/invoices.api';
import { apiErrorCode, apiErrorMessage } from '../../utils/apiError';
import { formatCurrencyFromDollars as money } from '../../utils/currency';

interface AmendTotalsPreviewProps {
  /** The invoice's total as it stands (shown until the first preview answers). */
  currentTotal: string;
  data: AmendPreview | undefined;
  /** A request for the CURRENT form is in flight (the figures shown may be the previous answer). */
  updating: boolean;
  error: unknown;
  /** Why no preview was asked for (e.g. a line still needs a price); null when one was. */
  waitingFor: string | null;
}

/** A refusal the save would also give, worded as "can't save yet". */
function refusalText(error: unknown): string {
  switch (apiErrorCode(error)) {
    case 'DUE_DATE_IN_PAST':
      return 'The new due date is in the past.';
    case 'PAYMENT_RECORDED':
      return 'A payment has been recorded against this invoice, so it can no longer be amended.';
    case 'STALE_INVOICE':
      return 'Someone else amended this invoice after you opened it. Close and reopen it.';
    default:
      return apiErrorMessage(error, 'Could not work out the new total.');
  }
}

export default function AmendTotalsPreview({
  currentTotal,
  data,
  updating,
  error,
  waitingFor,
}: AmendTotalsPreviewProps) {
  if (error && !updating) {
    return (
      <Alert severity="warning" data-testid="amend-totals">
        Can&apos;t save yet: {refusalText(error)}
      </Alert>
    );
  }

  const after = data?.after;
  const hasDiscounts = !!after && Number(after.totalDiscounts) > 0;
  const hasTax = !!after && Number(after.tax) > 0;

  return (
    <Box data-testid="amend-totals" sx={{ opacity: updating ? 0.6 : 1, transition: 'opacity 0.15s' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body1">
          Current total {money(data?.before.total ?? currentTotal)} →{' '}
          <strong>New total {after ? money(after.total) : '—'}</strong>
        </Typography>
        {updating && <CircularProgress size={14} aria-label="Updating totals" />}
      </Box>
      {after && (
        <Typography variant="body2" color="text.secondary">
          Subtotal {money(after.subtotal)}
          {hasDiscounts && <> · Discounts −{money(after.totalDiscounts)}</>}
          {/* #372: charged late fees stay through an amend (waive one if it is wrong). */}
          {after.lateFees && Number(after.lateFees) > 0 && <> · Late fees {money(after.lateFees)}</>}
          {hasTax && <> · GST {money(after.tax)}</>}
        </Typography>
      )}
      {waitingFor && (
        <Typography variant="body2" color="text.secondary">
          {waitingFor}
        </Typography>
      )}
      {data?.noChanges && !waitingFor && (
        <Typography variant="body2" color="text.secondary">
          No changes yet.
        </Typography>
      )}
    </Box>
  );
}
